import { useMutation, useQuery } from "@apollo/client";
import {
  Checkbox,
  Divider,
  Fab,
  FormControlLabel,
  makeStyles,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import { Skeleton } from "@material-ui/lab";
import { useSnackbar } from "notistack";
import React from "react";
import { useHistory, useParams } from "react-router";
import DeleteDialog from "../../components/DeleteDialog";
import LoadingError from "../../components/LoadingError";
import LocationDialog from "../../components/Location/LocationDialog";
import TextBox from "../../components/TextBox";
import { ROUTES } from "../../constants";
import { HealthDeparmentNumberLookup } from "../../constants/AccountingNumbers";
import { UPDATE_LOCATION } from "../../graphql/mutations";
import { GET_LOCATION } from "../../graphql/queries";
import { GetLocationQuery } from "../../graphql/queries/getLocation";
import {
  GetLocationQueryVariables,
  UpdateLocationMutation,
  UpdateLocationMutationVariables,
} from "../../graphql/types";
import useSiteTitle from "../../hooks/useSiteTitle";
import LocationEmployees from "./LocationEmployees";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },

  contentRoot: {
    display: "flex",
    flex: 1,
    padding: "40px 60px",
    width: "100%",
    flexDirection: "column",
    marginTop: 40,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  detailsHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",

    flex: 1,
    justifyContent: "space-between",
    paddingBottom: theme.spacing(2),
  },
  content: {
    marginBottom: theme.spacing(2),
  },
  address: {
    marginTop: 20,
    paddingLeft: 10,
  },
}));

const LocationDetails: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [showDialog, setShowDialog] = React.useState<string>("");
  const history = useHistory();
  const [showDeleteDialog, setShowDeleteDialog] =
    React.useState<boolean>(false);

  const { locationId: id, companyId } = useParams<{
    locationId: string;
    companyId: string;
  }>();
  const classes = useStyles();

  const { data, loading, error } = useQuery<
    GetLocationQuery,
    GetLocationQueryVariables
  >(GET_LOCATION, {
    variables: {
      id,
    },
    onError(error) {
      console.log(error);
      enqueueSnackbar("Fehler beim laden des Standorts", { variant: "error" });
    },
  });
  const [deactivate, { loading: deactivating }] = useMutation<
    UpdateLocationMutation,
    UpdateLocationMutationVariables
  >(UPDATE_LOCATION, {
    update(cache) {
      try {
        const normalizedId = cache.identify({
          id,
          __typename: "Location",
        });
        cache.evict({ id: normalizedId });
        cache.gc();
      } catch (error) {
        console.log(error);
      }
    },
    onError(error) {
      console.log(error);
      enqueueSnackbar("Fehler beim löschen des Standorts", {
        variant: "error",
      });
    },
    onCompleted() {
      enqueueSnackbar("Standort wurde gelöscht", { variant: "warning" });

      history.replace(`${ROUTES.COMPANIES}/${companyId}`);
    },
  });

  const { getLocation } = data || {};
  const {
    name,
    locationId,
    email,
    address,
    phone,
    enableAutoEmail,
    healthDeparmentNumber,
    healthDepartmentEmail,
  } = getLocation || {};

  useSiteTitle(`Standort - ${name || ""}`);
  const _handleCloseDialog = () => {
    setShowDialog("");
  };
  const _openDialog = () => {
    setShowDialog(id);
  };
  const _closeDeactivateDialog = () => {
    setShowDeleteDialog(false);
  };
  const _openDeactivateDialog = () => {
    setShowDeleteDialog(true);
  };
  return (
    <div className={classes.root}>
      <DeleteDialog
        open={showDeleteDialog}
        loading={deactivating}
        onSubmit={() => {
          deactivate({ variables: { input: { id, deactivated: true } } });
        }}
        handleClose={_closeDeactivateDialog}
        object="Standort"
      />
      <LocationDialog
        open={!!showDialog}
        id={id}
        companyId={companyId}
        handleClose={_handleCloseDialog}
        initialValues={{
          name: name!!,
          locationId: locationId!!,
          email,
          address,
          phone: phone || undefined,
          enableAutoEmail: enableAutoEmail || false,
          healthDeparmentNumber: healthDeparmentNumber || undefined,
          healthDepartmentEmail: healthDepartmentEmail || undefined,
        }}
      />
      <div className={classes.header}>
        <Typography variant="h3">
          {!loading && `Standort ${name}`}
          {loading && <Skeleton variant="text" width={700} height={80} />}
        </Typography>
        {!loading && (
          <div>
            <Tooltip title="Standort löschen" style={{ marginRight: 20 }}>
              <Fab
                aria-label="update company"
                size={"small"}
                disabled={loading}
                onClick={_openDeactivateDialog}
                color={"primary"}
              >
                <DeleteIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Standort bearbeiten">
              <Fab
                aria-label="update location"
                size={"small"}
                disabled={loading}
                onClick={_openDialog}
                color={"primary"}
              >
                <EditIcon />
              </Fab>
            </Tooltip>
          </div>
        )}
      </div>
      {!loading && !!error && (
        <LoadingError text="Der Auftraggeber konnte nicht geladen werden" />
      )}
      {!error && loading && (
        <>
          <Skeleton variant="rect" height={600} style={{ marginTop: 20 }} />
        </>
      )}
      {!loading && !error && (
        <Paper className={classes.contentRoot}>
          <div className={classes.detailsHeader}>
            <Typography variant="h5">Details</Typography>
          </div>
          <Divider />
          <div className={classes.content}>
            <TextBox label="Name" value={name || "-"} bordered={false} />
            <TextBox
              label="Standort-ID"
              value={locationId || "-"}
              bordered={false}
            />
            <TextBox
              label="Standort Email"
              value={email || "-"}
              bordered={false}
            />
            <TextBox
              label="Standort Telefonnummer"
              value={phone || "-"}
              bordered={false}
            />

            <Typography variant="overline">Auto Reporting</Typography>
            <Divider />
            <TextBox
              label="Gesundheitsamt eMail"
              value={healthDepartmentEmail || "-"}
              bordered={false}
            />
            <TextBox
              label="Abrechnungsnummer"
              value={
                HealthDeparmentNumberLookup[healthDeparmentNumber || ""] || "-"
              }
              bordered={false}
            />
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enableAutoEmail}
                    color="primary"
                    disabled
                    inputProps={{ "aria-label": "selftests checkbox" }}
                  />
                }
                label="Übermittlung aktiviert"
              />
            </div>

            <Typography variant="overline">Adresse</Typography>
            <Divider />

            <div className={classes.address}>
              {address ? (
                <>
                  <TextBox
                    label="Straße "
                    value={`${address?.street || "-"} ${
                      address?.number || "-"
                    }`}
                    bordered={false}
                  />
                  <TextBox
                    label="Ort und PLZ "
                    value={`${address?.city || "-"} ${address?.zip || "-"}`}
                    bordered={false}
                  />
                </>
              ) : (
                <Typography variant="body1">
                  Keine Adresse hinterlegt
                </Typography>
              )}
            </div>
          </div>
        </Paper>
      )}
      <LocationEmployees locationId={id} />
    </div>
  );
};

export default LocationDetails;
